import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MemberCard from './MemberCard';
import UnassignedMembersSection from './UnassignedMembersSection';
import { calculateUnassignedMembers, sortMembersByRole } from './utils';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import Modal from '@/components/common/Modal';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from '@/gql/graphql';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

/**
 * Props for ViewGroupMembersForm component
 */
export interface ViewGroupMembersProps {
    open: boolean;
    group?: GroupListItemDataFragment;
    subgroup?: SubgroupListItemDataFragment;
    onClose: () => void;
}

// Helper function to build footer text for subgroups and unassigned members
const buildFooterText = (
    subgroupCount: number,
    unassignedCount: number,
    t: ReturnType<typeof useTranslation<'groups'>>['t']
): string => {
    // If only unassigned members (no subgroups), use special format with capitalized "Unassigned Members"
    if (subgroupCount === 0 && unassignedCount > 0) {
        return t('viewMembersModal.showingUnassignedMembers', { count: unassignedCount });
    }

    const parts: string[] = [];

    if (subgroupCount > 0) {
        parts.push(
            `${subgroupCount} ${subgroupCount === 1 ? t('viewMembersModal.subgroup') : t('viewMembersModal.subgroups')}`
        );
    }

    if (unassignedCount > 0) {
        parts.push(
            `${unassignedCount} ${unassignedCount === 1 ? t('viewMembersModal.unassignedMember') : t('viewMembersModal.unassignedMembers')}`
        );
    }

    if (parts.length > 0) {
        const details = parts.join(` ${t('viewMembersModal.and')} `);
        return t('viewMembersModal.showingSubgroupsAndUnassigned', { details });
    }

    return '';
};

const ViewGroupMembers = ({ open, onClose, group, subgroup }: ViewGroupMembersProps) => {
    const { t } = useTranslation('groups');
    const { getRoleLabel } = useCompanyRoleLabels();

    const subgroupsWithMembers = useMemo(() => {
        if (!group) {
            return [];
        }

        // If viewing a specific subgroup
        if (subgroup) {
            const memberIdSet = new Set(subgroup.memberUserIds);
            const members = group.allMembers.filter(member => memberIdSet.has(member.id)).sort(sortMembersByRole);
            return [{ ...subgroup, members }];
        }

        // If viewing all subgroups
        if (group.subgroups && group.subgroups.length > 0) {
            return group.subgroups.map(sg => {
                const memberIdSet = new Set(sg.memberUserIds);
                const members = group.allMembers.filter(member => memberIdSet.has(member.id)).sort(sortMembersByRole);
                return {
                    ...sg,
                    members,
                };
            });
        }

        return [];
    }, [group, subgroup]);

    // Calculate unassigned members (members in group but not in any subgroup)
    const unassignedMembers = useMemo(() => {
        return calculateUnassignedMembers(group, subgroup);
    }, [group, subgroup]);

    const totalMembers = useMemo(() => {
        // Count unique members from subgroups and unassigned
        const uniqueMemberIds = new Set<string>();
        subgroupsWithMembers.forEach(sg => {
            sg.members.forEach(member => {
                uniqueMemberIds.add(member.id);
            });
        });
        unassignedMembers.forEach(member => {
            uniqueMemberIds.add(member.id);
        });
        return uniqueMemberIds.size;
    }, [subgroupsWithMembers, unassignedMembers]);

    const hasContent = subgroupsWithMembers.length > 0 || unassignedMembers.length > 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            noAction
            title={subgroup?.name ?? group?.name ?? ''}
            largeTitle
            className="!max-h-[542px] !w-[512px] !max-w-[512px] !px-5 !py-5"
        >
            {hasContent ? (
                <div className="-mx-5 flex flex-col gap-5">
                    {/* Scrollable content area */}
                    <div className="flex max-h-[390px] flex-col overflow-y-auto">
                        {/* Unassigned Members Section */}
                        <UnassignedMembersSection members={unassignedMembers} getRoleLabel={getRoleLabel} />

                        {/* Subgroups */}
                        {subgroupsWithMembers.map(sg => (
                            <Disclosure key={sg.id} defaultOpen={false}>
                                {({ open }) => (
                                    <>
                                        {/* Subgroup Header */}
                                        <DisclosureButton className="flex w-full items-center justify-between gap-2.5 border-t-[1px] border-neutral-300 bg-neutral-50 p-5 text-left hover:bg-neutral-100">
                                            <div className="flex-1 leading-6 font-bold text-neutral-900">{sg.name}</div>
                                            <ChevronDown
                                                className={clsx(
                                                    'size-4 text-neutral-800 transition-transform',
                                                    open && 'rotate-180'
                                                )}
                                            />
                                        </DisclosureButton>

                                        {/* Members */}
                                        <DisclosurePanel>
                                            {sg.members.map((member, index) => (
                                                <MemberCard
                                                    key={member.id}
                                                    member={member}
                                                    getRoleLabel={getRoleLabel}
                                                    isLast={index === sg.members.length - 1}
                                                />
                                            ))}
                                        </DisclosurePanel>
                                    </>
                                )}
                            </Disclosure>
                        ))}
                    </div>

                    {/* Sticky Footer - always visible */}
                    <div className="flex items-center justify-center gap-2.5 px-5">
                        <div className="flex-1 leading-6 font-medium text-neutral-500">
                            {subgroup
                                ? t('viewMembersModal.showingMembers', { count: totalMembers })
                                : buildFooterText(subgroupsWithMembers.length, unassignedMembers.length, t)}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="-mx-5 px-5">
                    <EmptyContainer>
                        <Empty title={t('viewMembersModal.empty.title')} />
                    </EmptyContainer>
                </div>
            )}
        </Modal>
    );
};

export default ViewGroupMembers;
